import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Mail, ArrowLeft, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error('Por favor introduce tu correo electrónico');
            return;
        }

        setLoading(true);
        try {
            await api.post('/auth/forgot-password', { email });
            setSubmitted(true);
            toast.success('Instrucciones enviadas');
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al procesar la solicitud';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-wedding-background flex items-center justify-center p-8">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-md bg-white p-8 rounded-2xl shadow-luxury border border-wedding-border"
            >
                <Link to="/login" className="flex items-center gap-2 mb-8 text-wedding-text-muted hover:text-wedding-primary transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                    <span>Volver al inicio de sesión</span>
                </Link>

                <div className="flex items-center gap-2 mb-6 justify-center">
                    <Heart className="w-6 h-6 text-wedding-primary" fill="currentColor" />
                    <span className="font-heading text-2xl text-wedding-text">Wedvites</span>
                </div>

                {!submitted ? (
                    <>
                        <div className="text-center mb-8">
                            <h1 className="font-heading text-3xl text-wedding-text mb-2">
                                ¿Olvidaste tu contraseña?
                            </h1>
                            <p className="text-wedding-text-muted">
                                No te preocupes, introduce tu correo y te enviaremos las instrucciones para restablecerla.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-wedding-text">
                                    Correo Electrónico
                                </Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-wedding-text-muted" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="tu@email.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10 py-6 border-wedding-border focus:border-wedding-primary focus:ring-wedding-primary"
                                        required
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full py-6 bg-wedding-primary hover:bg-wedding-primary-dark text-white rounded-full text-sm uppercase tracking-widest font-semibold transition-all duration-300"
                            >
                                <Send className="w-4 h-4 mr-2" />
                                {loading ? 'Enviando...' : 'Enviar Instrucciones'}
                            </Button>
                        </form>
                    </>
                ) : (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600">
                            <Send className="w-8 h-8" />
                        </div>
                        <h2 className="font-heading text-2xl text-wedding-text mb-4">¡Correo enviado!</h2>
                        <p className="text-wedding-text-muted mb-8 text-pretty">
                            Si el correo <strong>{email}</strong> está registrado, recibirás un enlace para crear una nueva contraseña en unos momentos.
                        </p>
                        <Link to="/login">
                            <Button variant="outline" className="w-full rounded-full border-wedding-primary text-wedding-primary">
                                Volver al Login
                            </Button>
                        </Link>
                    </div>
                )}
            </motion.div>
        </div>
    );
}
